import classNames from 'classnames';
import React from 'react';

import {assert} from 'Asserts';
import {OligoAwareFolderItemType} from 'constants/RegistryConstants';
import {FileSummary} from 'folder-items/FileConstants';
import * as FileUtil from 'folder-items/FileUtil';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import * as RegistryValidatorActions from 'registry/RegistryValidatorActions';
import {
  GetFullTagSchemaQuery,
  GetFullTagSchemaQueryVariables,
} from 'schemas/__generated/GetFullTagSchemaQuery';
import {GET_FULL_TAG_SCHEMA_QUERY} from 'schemas/useTagSchemaQuery';
import {useTagSchemasListingQuery} from 'schemas/useTagSchemasListingQuery';
import {toOligoAwareFolderItemTypeGraphQL} from 'tag-schema/util/ModelTypeConversions';
import * as TagSchemaUtil from 'tag-schema/util/TagSchemaUtil';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import LegacyDropdownMenu from 'ui-library/dropdowns/legacy/LegacyDropdownMenu';
import LegacyDropdownMenuItem from 'ui-library/dropdowns/legacy/LegacyDropdownMenuConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {fileToOligoAwareFolderItemType} from 'util/APIUtil';
import {typeToName} from 'util/NameUtil';

import {TagSchemaListingGraphQL} from '../../../../graphql-fragments/schemas/__generated/TagSchemaListingGraphQL';

/** @package */
export type TagSchemaOptions = {data: Array<TagSchemaListingGraphQL>} | {error: string};

/** @package */
export interface QueryingRegisterDropdownProps {
  className?: string;
  files: Array<FileSummary>;
  disabled?: boolean;
  // This callback aims to track item selections.
  onClickTracking?: (selectedItemName: string) => void;
}

/** @package */
export interface RegisterDropdownProps extends QueryingRegisterDropdownProps {
  tagSchemaOptions: TagSchemaOptions | null;
  setTagSchema: (id: string) => Promise<void>;
}

/** @package */
export class RegisterDropdown extends React.Component<RegisterDropdownProps> {
  private getRegisterError = (): {
    someFilesRegistered: boolean;
    noTagSchemaSet: boolean;
    noTagSchemaRegisterPermission: boolean;
    errorMessage: string | undefined;
  } => {
    const noTagSchemaSet = !TagSchemaUtil.isTagSchemaSet(this.props.files);
    const noTagSchemaRegisterPermission =
      !noTagSchemaSet && !this.props.files.every((file) => file.tagSchema?.permissions.objectsRegisterable);
    const someFilesRegistered = this.props.files.some((file) => FileUtil.isInRegistry(file));
    let errorMessage;
    if (noTagSchemaSet) {
      errorMessage = 'Register after setting schema';
    } else if (noTagSchemaRegisterPermission) {
      errorMessage = 'Need register permission for some selected files';
    } else if (someFilesRegistered) {
      errorMessage = 'Some of these files are already registered';
    }

    return {
      someFilesRegistered,
      noTagSchemaSet,
      noTagSchemaRegisterPermission,
      errorMessage,
    };
  };

  private handleOpenRegistryValidatorModal = (): void => {
    this.props.onClickTracking?.('Register');
    RegistryValidatorActions.openRegistryValidatorModal(this.props.files);
  };

  private getSchemaMenuItems(
    schemaOptions: Array<TagSchemaListingGraphQL>
  ): Array<Array<LegacyDropdownMenuItem>> {
    const menuItems: Array<LegacyDropdownMenuItem> = [];

    for (const schemaOption of schemaOptions) {
      const menuItem = {
        name: schemaOption.name,
        handleOnClick: () => {
          this.props.onClickTracking?.('SetSchema');
          this.props.setTagSchema(schemaOption.id);
        },
      };
      menuItems.push(menuItem);
    }
    return [menuItems];
  }

  private getMenuItems(): Array<Array<LegacyDropdownMenuItem>> {
    if (this.props.tagSchemaOptions == null) {
      return [];
    }

    let submenu;

    const schemaOptions = this.props.tagSchemaOptions;
    // eslint-disable-next-line benchlint/no-in-operator
    if ('data' in schemaOptions) {
      submenu = this.getSchemaMenuItems(schemaOptions.data);
    } else {
      submenu = [
        [
          {
            name: schemaOptions.error,
            disabled: true,
          },
        ],
      ];
    }

    const {someFilesRegistered, noTagSchemaSet, noTagSchemaRegisterPermission, errorMessage} =
      this.getRegisterError();
    return [
      [
        // Disallow setting schemas on registered files
        someFilesRegistered
          ? {
              name: 'Cannot set schema on entities that are already registered',
              disabled: true,
            }
          : {
              name: 'Set schema',
              dropLeft: true,
              submenu,
            },
        someFilesRegistered || noTagSchemaSet || noTagSchemaRegisterPermission
          ? {
              name: errorMessage!,
              disabled: true,
            }
          : {
              name: 'Register',
              handleOnClick: this.handleOpenRegistryValidatorModal,
            },
      ],
    ];
  }

  override render(): JSX.Element {
    return (
      <Tooltip container="body" placement={TooltipPlacement.TOP} title="Register...">
        <div className={classNames('buttonGroup', this.props.className)}>
          <button
            aria-label="Register"
            className={classNames('button button--tertiaryWhite button--small dropdown-toggle', {
              'is-disabled': this.props.disabled,
            })}
            data-toggle="dropdown"
            disabled={this.props.disabled}
            type="button"
          >
            <BenchlingIcon icon="register" />
            <span className="caret" />
          </button>
          {!this.props.disabled && (
            <LegacyDropdownMenu className="dropdown-menu--dropLeft" items={this.getMenuItems()} />
          )}
        </div>
      </Tooltip>
    );
  }
}

function getCommonFileType(files: Array<FileSummary>): OligoAwareFolderItemType | null {
  if (files.length === 0) {
    return null;
  }

  let commonFileType: OligoAwareFolderItemType | null = null;

  for (const file of files) {
    const oligoAwareFolderItemType = fileToOligoAwareFolderItemType(file);
    if (commonFileType && oligoAwareFolderItemType !== commonFileType) {
      return null;
    } else {
      commonFileType = oligoAwareFolderItemType;
    }
  }

  return commonFileType;
}

function useTagSchemaOptionsForFiles(files: Array<FileSummary>): TagSchemaOptions | null {
  const commonFileType = getCommonFileType(files);
  const oligoAwareFolderItemTypes = commonFileType
    ? [toOligoAwareFolderItemTypeGraphQL(commonFileType)]
    : null;

  const skip = oligoAwareFolderItemTypes == null;
  const tagSchemasResult = useTagSchemasListingQuery({skip, oligoAwareFolderItemTypes});

  if (files.length > 0 && commonFileType == null) {
    return {error: 'Select files of a single type'};
  } else if (commonFileType == null || tagSchemasResult == null) {
    /**
     * One of the following is true:
     * - No files were selected, so no options are needed at this time
     * - Tag schemas are being fetched, so no options are available yet
     *
     * In both cases, we return null to indicate that the dropdown should be disabled.
     */
    return null;
  } else {
    const filteredTagSchemas = tagSchemasResult.tagSchemas.filter(
      ({permissions}) => permissions.objectsRegisterable && permissions.objectsCreatable
    );

    if (filteredTagSchemas.length > 0) {
      /**
       * We only reach this case if the following are true:
       * 1. At least one file is selected
       * 2. The files are all of a single type
       * 3. There are tag schemas available for that type
       * 4. The tag schemas have been fetched by query or loaded from cache
       * 5. The tag schemas have both register and create permissions
       */
      return {data: filteredTagSchemas};
    } else {
      return {error: `No schemas exist for ${typeToName(commonFileType)}`};
    }
  }
}

export default function QueryingRegisterDropdown(props: QueryingRegisterDropdownProps): JSX.Element {
  const tagSchemaOptions = useTagSchemaOptionsForFiles(props.files);

  async function setTagSchema(schemaId: string): Promise<void> {
    const result = await doQuerySafe<GetFullTagSchemaQuery, GetFullTagSchemaQueryVariables>(
      {query: GET_FULL_TAG_SCHEMA_QUERY},
      {variables: {id: schemaId}}
    );

    assert(!isInaccessible(result.tagSchema), 'Expected tag schema to be accessible.');
    TagSchemaUtil.bulkSetTagSchema(props.files, graphQLToREST('TagSchema', result.tagSchema));
  }

  return (
    <RegisterDropdown
      {...props}
      disabled={props.disabled || tagSchemaOptions == null}
      tagSchemaOptions={tagSchemaOptions}
      setTagSchema={setTagSchema}
    />
  );
}
